/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Spin, notification, Tooltip } from 'antd';
import get from 'lodash/get';
import {
  DAILY_AI_FEATURE_IDS,
  SPACER_LOG_AI_FEATURES,
  fetchAiInsightFeatures,
  generateAiInsight,
} from '../../services/aiInsightsApi';
import SpacerLogMarkdown from './SpacerLogMarkdown';

const FALLBACK_FEATURES = [
  {
    feature: SPACER_LOG_AI_FEATURES.PERIOD_BRIEF,
    title: 'Period brief',
    description: 'Narrative summary of period KPIs, pipelines, and risks.',
    maturity: 'P0',
    requiredContext: ['projectId', 'anchorDate', 'periodType'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.SHIFT_BRIEF,
    title: 'Day / shift brief',
    description: 'Narrative summary of daily KPIs, issues, bottlenecks, and actions.',
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH,
    title: 'Pace coach',
    description: 'Coaching on joint stage pace and bottlenecks.',
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMMENT_THEMES,
    title: 'Theme comments',
    description: 'Themes across downtime comments for the day.',
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT,
    title: 'Close-out check',
    description: 'Compliance gaps for the selected day in plain language.',
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN,
    title: 'Explain data issues',
    description: 'Explain unusual joint or KPI patterns for the day.',
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
];

const sessionCache = new Map();

const cacheKey = (feature, ctx) =>
  [
    feature,
    ctx.projectId,
    ctx.sowId ?? '',
    ctx.logDate ?? '',
    ctx.shift ?? '',
    ctx.anchorDate ?? '',
    ctx.periodType ?? '',
  ].join(':');

const normalizeShift = (shift) => {
  if (shift === '0' || shift === 0) return 0;
  if (shift === '1' || shift === 1) return 1;
  return 'both';
};

const missingContext = (required = [], ctx) => {
  const missing = [];
  required.forEach((key) => {
    const val = ctx[key];
    if (val == null || val === '') {
      missing.push(key);
      return;
    }
    if (key === 'sowId' && (val === 'all' || Number.isNaN(Number(val)))) {
      missing.push('sowId (select a pipeline)');
    }
  });
  return missing;
};

const SpacerLogAiInsightPanel = ({
  open,
  onClose,
  initialFeature,
  tabScope = 'daily',
  context = {},
}) => {
  const [featuresMeta, setFeaturesMeta] = useState(null);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(initialFeature || SPACER_LOG_AI_FEATURES.SHIFT_BRIEF);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const aiEnabled = featuresMeta ? featuresMeta.enabled !== false : true;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingFeatures(true);
    fetchAiInsightFeatures()
      .then((data) => {
        if (!cancelled) setFeaturesMeta(data);
      })
      .catch(() => {
        if (!cancelled) {
          setFeaturesMeta({
            enabled: false,
            ragConfigured: false,
            features: FALLBACK_FEATURES,
            provider: null,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFeatures(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open && initialFeature) {
      setSelectedFeature(initialFeature);
      setResult(null);
    }
  }, [open, initialFeature]);

  useEffect(() => {
    if (!open) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setGenerating(false);
      setElapsedSec(0);
    }
  }, [open]);

  const scopedFeatures = useMemo(() => {
    const apiList = featuresMeta?.features?.length ? featuresMeta.features : FALLBACK_FEATURES;
    const allow =
      tabScope === 'overview'
        ? new Set([SPACER_LOG_AI_FEATURES.PERIOD_BRIEF])
        : new Set(DAILY_AI_FEATURE_IDS);
    return apiList.filter((f) => allow.has(f.feature));
  }, [featuresMeta, tabScope]);

  useEffect(() => {
    if (!scopedFeatures.length) return;
    if (!scopedFeatures.some((f) => f.feature === selectedFeature)) {
      setSelectedFeature(scopedFeatures[0].feature);
    }
  }, [scopedFeatures, selectedFeature]);

  useEffect(() => {
    if (!open || !selectedFeature) return;
    const key = cacheKey(selectedFeature, context);
    if (sessionCache.has(key)) {
      setResult(sessionCache.get(key));
    }
    // Intentionally key off primitive context fields (parent builds a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedFeature,
    context.projectId,
    context.sowId,
    context.logDate,
    context.shift,
    context.anchorDate,
    context.periodType,
  ]);

  const selectedMeta = scopedFeatures.find((f) => f.feature === selectedFeature) || null;
  const isStub = selectedMeta?.status === 'stub';
  const ctxMissing = missingContext(selectedMeta?.requiredContext || [], context);

  const buildBody = useCallback(
    (mode) => {
      const body = {
        feature: selectedFeature,
        mode,
        projectId: context.projectId,
      };
      if (tabScope === 'overview') {
        body.anchorDate = context.anchorDate;
        body.periodType = context.periodType;
        if (context.sowId != null) body.sowId = context.sowId;
      } else {
        body.sowId = Number(context.sowId);
        body.logDate = context.logDate;
        body.shift = normalizeShift(context.shift);
      }
      return body;
    },
    [selectedFeature, context, tabScope]
  );

  const runGenerate = useCallback(
    async (mode = 'ai') => {
      if (!selectedFeature || ctxMissing.length) return;
      if (mode === 'ai' && !aiEnabled) {
        mode = 'template';
      }
      if (isStub && mode === 'ai') {
        notification.info({ message: 'This insight is coming soon.' });
        return;
      }

      const key = cacheKey(selectedFeature, context);
      if (mode === 'ai' && sessionCache.has(key)) {
        setResult(sessionCache.get(key));
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setGenerating(true);
      setElapsedSec(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);

      try {
        const data = await generateAiInsight(buildBody(mode), { signal: controller.signal });
        setResult(data);
        if (mode === 'ai' && data?.markdown) {
          sessionCache.set(key, data);
        }
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
          return;
        }
        const msg = get(err, 'error', get(err, 'message', 'Failed to generate insight.'));
        notification.error({ message: msg });
      } finally {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setGenerating(false);
        abortRef.current = null;
      }
    },
    [selectedFeature, ctxMissing.length, aiEnabled, isStub, context, buildBody]
  );

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleCopy = async () => {
    const md = result?.markdown;
    if (!md) return;
    try {
      await navigator.clipboard.writeText(md);
      notification.success({ message: 'Copied insight to clipboard.' });
    } catch {
      notification.error({ message: 'Could not copy to clipboard.' });
    }
  };

  const footerMeta = useMemo(() => {
    if (!result) return null;
    const parts = [];
    if (result.generatedAt) {
      try {
        parts.push(
          new Date(result.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        );
      } catch {
        parts.push(result.generatedAt);
      }
    }
    if (result.provider) parts.push(result.provider);
    if (result.mode === 'template') parts.push('template');
    return parts.join(' · ');
  }, [result]);

  const showFallbackBanner =
    result && (result.fallback === true || result.mode === 'template');

  return (
    <Drawer
      placement="right"
      width={440}
      visible={open}
      onClose={onClose}
      destroyOnClose={false}
      className="spacer-log-ai-drawer"
      title={
        <div className="spacer-log-ai-drawer__title-wrap">
          <span className="spacer-log-ai-drawer__eyebrow">Spacer Log</span>
          <span className="spacer-log-ai-drawer__title">AI Insights</span>
        </div>
      }
    >
      <div className="spacer-log-ai-panel">
        {!aiEnabled ? (
          <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
            AI temporarily unavailable — template briefs still work.
          </div>
        ) : null}

        <div className="spacer-log-ai-chips">
          {loadingFeatures && !scopedFeatures.length ? (
            <Spin size="small" />
          ) : (
            scopedFeatures.map((f) => {
              const stub = f.status === 'stub';
              const active = f.feature === selectedFeature;
              const chip = (
                <button
                  key={f.feature}
                  type="button"
                  className={`spacer-log-ai-chip${active ? ' spacer-log-ai-chip--active' : ''}${
                    stub ? ' spacer-log-ai-chip--stub' : ''
                  }`}
                  disabled={stub && !active}
                  title={f.description || f.title}
                  onClick={() => {
                    setSelectedFeature(f.feature);
                    setResult(null);
                  }}
                >
                  {f.title || f.feature}
                  {stub ? <span className="spacer-log-ai-chip__badge">Soon</span> : null}
                </button>
              );
              return stub ? (
                <Tooltip key={f.feature} title="Coming soon">
                  {chip}
                </Tooltip>
              ) : (
                chip
              );
            })
          )}
        </div>

        {selectedMeta?.description ? (
          <p className="spacer-log-ai-desc">{selectedMeta.description}</p>
        ) : null}

        {ctxMissing.length ? (
          <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
            {tabScope === 'daily' && ctxMissing.some((m) => String(m).includes('sowId'))
              ? 'Select a pipeline day first.'
              : `Missing context: ${ctxMissing.join(', ')}`}
          </div>
        ) : null}

        <div className="spacer-log-ai-actions">
          <button
            type="button"
            className="spacer-log-ai-btn spacer-log-ai-btn--primary"
            disabled={generating || !!ctxMissing.length || isStub}
            onClick={() => runGenerate(aiEnabled ? 'ai' : 'template')}
          >
            Generate
          </button>
          <button
            type="button"
            className="spacer-log-ai-btn"
            disabled={generating || !!ctxMissing.length}
            onClick={() => runGenerate('template')}
          >
            Template only
          </button>
          {generating ? (
            <button type="button" className="spacer-log-ai-btn" onClick={handleCancel}>
              Cancel
            </button>
          ) : null}
        </div>

        {generating ? (
          <div className="spacer-log-ai-loading">
            <Spin size="small" />
            <span>Generating… {elapsedSec}s</span>
          </div>
        ) : null}

        {showFallbackBanner ? (
          <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
            {result.fallback
              ? 'AI unavailable — showing template brief'
              : 'Deterministic brief (template)'}
            {result.error ? ` · ${result.error}` : ''}
          </div>
        ) : null}

        <div className="spacer-log-ai-body">
          {result?.markdown ? (
            <SpacerLogMarkdown content={result.markdown} />
          ) : !generating ? (
            <p className="spacer-log-md__muted">
              Choose a feature and click Generate for an on-demand brief. Filters from the dashboard
              are used — nothing is auto-run.
            </p>
          ) : null}
        </div>

        {result?.markdown ? (
          <div className="spacer-log-ai-footer">
            <span className="spacer-log-ai-footer__meta">{footerMeta || '—'}</span>
            <div className="spacer-log-ai-footer__actions">
              <button type="button" className="spacer-log-ai-btn" onClick={handleCopy}>
                Copy
              </button>
              <button
                type="button"
                className="spacer-log-ai-btn"
                disabled={generating || !!ctxMissing.length}
                onClick={() => {
                  sessionCache.delete(cacheKey(selectedFeature, context));
                  runGenerate(aiEnabled ? 'ai' : 'template');
                }}
              >
                Regenerate
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
};

export default SpacerLogAiInsightPanel;
